import React from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import Figure from "../ui/Figure";
import Reveal from "../ui/Reveal";
import { ArrowLink } from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { COMPANY } from "../../data/company";
import { color, font, type } from "../../design/tokens";

/**
 * The trust section, immediately under the hero.
 *
 * An asymmetric three-part layout: a statement set at display size in the left
 * two-thirds, a narrow supporting column, and a pair of offset plates. The
 * offset is the point — nothing in this design sits in a neat symmetrical row.
 *
 * The one figure quoted here is the company's own published claim about
 * projects completed, and it's attributed that way rather than presented as an
 * audited statistic.
 */
const TrustStatement = () => (
  <Section tone="paper">
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1.25fr 0.75fr" },
        gap: { xs: 6, lg: 10 },
        alignItems: "start",
      }}
    >
      <Box>
        <Label>Why homeowners call us</Label>
        <Display as="h2" size="lg" sx={{ marginTop: 3, maxWidth: 880 }}>
          Most renovation stress comes from not knowing what's happening.
          We built the way we work around{" "}
          <Accent>removing that</Accent>.
        </Display>
      </Box>

      <Box sx={{ paddingTop: { lg: 2.5 } }}>
        <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0 }}>
          One crew, self-performing most of the trades. A written scope and a
          real timeline before anything is committed. Permits and inspections
          handled by us. Containment up before demolition starts, and a site
          that gets tidied at the end of every day.
        </Box>
        <Box component="p" sx={{ ...type.body, color: color.graphite, marginTop: 2.5 }}>
          It's not a sales pitch so much as a description — most of what
          separates a good renovation from a bad one is organisational, not
          technical.
        </Box>
        <Box sx={{ marginTop: 4 }}>
          <ArrowLink to="/about">More about how we work</ArrowLink>
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(12, 1fr)" },
        gap: { xs: 4, lg: 5 },
        marginTop: { xs: 8, md: 12 },
        alignItems: "end",
      }}
    >
      <Box sx={{ gridColumn: { lg: "1 / span 5" } }}>
        <Figure media="basementHallway" ratio="4 / 5" />
      </Box>

      <Box
        sx={{
          gridColumn: { lg: "7 / span 4" },
          // Drops the second plate below the first — the offset that keeps
          // the pair from reading as a two-up grid.
          marginTop: { lg: 10 },
        }}
      >
        <Figure media="bathMarbleDetail" ratio="1 / 1" />
      </Box>

      <Box sx={{ gridColumn: { lg: "12 / span 1" }, display: { xs: "none", lg: "block" } }} />
    </Box>

    <Reveal>
      <Box
        sx={{
          marginTop: { xs: 7, md: 10 },
          paddingTop: 5,
          borderTop: `1px solid ${color.line}`,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: { xs: 4, md: 6 },
        }}
      >
        {[
          {
            figure: COMPANY.projectsCompleted,
            label: "Renovation projects completed",
            note: "As stated by Five Lights Reno",
          },
          {
            figure: "9",
            label: "Trades handled in-house",
            note: "From framing through to final coat",
          },
          {
            figure: "4",
            label: "Core service areas",
            note: "Pickering, Ajax, Whitby, Durham Region",
          },
        ].map((stat) => (
          <Box key={stat.label}>
            <Box
              component="p"
              sx={{
                fontFamily: font.display,
                fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                lineHeight: 1,
                color: color.ink,
                margin: 0,
              }}
            >
              {stat.figure}
            </Box>
            <Box
              component="p"
              sx={{ ...type.body, color: color.ink, fontWeight: 600, marginTop: 1.5 }}
            >
              {stat.label}
            </Box>
            <Box component="p" sx={{ ...type.caption, color: color.mute, marginTop: 0.75 }}>
              {stat.note}
            </Box>
          </Box>
        ))}
      </Box>
    </Reveal>
  </Section>
);

export default TrustStatement;
